import React, { useState, useId } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Percent,
  Search,
  RotateCcw,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  Sparkles,
  Send,
  Loader2,
  Tag,
  PackageCheck,
  Check,
  CheckSquare,
  Square,
  Layers,
  X,
  History,
} from 'lucide-react';
import {
  fetchMargeControl,
  updateMargeControlPrice,
  bulkUpdateMargeControlPrice,
  type MargeControlRowDto,
  type MargeControlTotalsDto,
  fetchPriceHistory,
} from '../api.js';
import { PageHeader } from '../ui/PageHeader.js';
import { Button } from '../ui/Button.js';
import { TableShell, Table, THead, TBody, Tr, Th, Td } from '../ui/table.js';
import { Loading } from '../ui/feedback.js';
import { formatMillimes } from './CompetitorPricesPage.js';
import { SubBrandPills } from '../components/SubBrandPills.js';

/** Formats a decimal number (in Dinars) into 3-digit millimes for clean input editing. */
function dinarsToMillimes(dinarsStr: string): number | null {
  const normalized = dinarsStr.replace(',', '.').trim();
  const num = parseFloat(normalized);
  if (Number.isNaN(num) || num < 0) return null;
  return Math.round(num * 1000);
}

function millimesToDinars(millimes: number): string {
  return (millimes / 1000).toFixed(3);
}

/**
 * Computes target regular selling price based on cost TTC and markup percentage.
 * In Tunisian retail, prices always end with 0 at the last millime digit (multiple of 10 millimes).
 * E.g. 17.369 DT + 20% = 20.8428 DT => 20.840 DT (20840 millimes).
 */
export function computeTargetMarkupPriceMillimes(costMillimes: number, targetMarkupPct: number): number {
  if (costMillimes <= 0 || targetMarkupPct <= 0) return costMillimes;
  const rawMillimes = costMillimes * (1 + targetMarkupPct / 100);
  return Math.round(rawMillimes / 10) * 10;
}

interface PriceAdjustmentCellProps {
  readonly row: MargeControlRowDto;
  readonly onUpdated: () => void;
}

function PriceAdjustmentCell({ row, onUpdated }: PriceAdjustmentCellProps): React.ReactElement {
  const initialDinars = millimesToDinars(row.sellingPriceTtc);
  const [dinarsInput, setDinarsInput] = useState<string>(initialDinars);
  const [feedback, setFeedback] = useState<{ tone: 'ok' | 'error'; message: string } | null>(null);

  // Compute live prospective markup & margin based on current typed price
  const candidateMillimes = dinarsToMillimes(dinarsInput);
  const prospectiveMarkup =
    candidateMillimes && candidateMillimes > 0 && row.effectiveCostTtc > 0
      ? Math.round(((candidateMillimes - row.effectiveCostTtc) / row.effectiveCostTtc) * 1000) / 10
      : null;
  const prospectiveMargin =
    candidateMillimes && candidateMillimes > 0
      ? Math.round(((candidateMillimes - row.effectiveCostTtc) / candidateMillimes) * 1000) / 10
      : null;

  const mutation = useMutation({
    mutationFn: updateMargeControlPrice,
    onSuccess: (res) => {
      setFeedback({ tone: 'ok', message: res.message || 'Prix mis à jour sur WooCommerce !' });
      onUpdated();
      setTimeout(() => setFeedback(null), 4000);
    },
    onError: (err: unknown) => {
      const errObj = (typeof err === 'object' && err !== null ? err : {}) as { message?: string };
      setFeedback({
        tone: 'error',
        message: errObj.message || 'Échec de mise à jour WooCommerce',
      });
      setTimeout(() => setFeedback(null), 5000);
    },
  });

  const handleApplyPreset = (targetMarkupPct: number) => {
    if (row.effectiveCostTtc <= 0) return;
    const computedMillimes = computeTargetMarkupPriceMillimes(row.effectiveCostTtc, targetMarkupPct);
    setDinarsInput(millimesToDinars(computedMillimes));
  };

  const handlePush = () => {
    if (!candidateMillimes || candidateMillimes <= 0) {
      setFeedback({ tone: 'error', message: 'Prix invalide' });
      return;
    }
    mutation.mutate({
      productId: row.productId,
      regularPriceTtc: candidateMillimes,
      salePriceTtc: null, // resets promo when adjusting standard base price
    });
  };

  return (
    <div className="flex flex-col gap-2 min-w-[200px]">
      <div className="flex items-center gap-1">
        <span className="text-[11px] font-medium text-ink-muted">Cibles :</span>
        <button
          type="button"
          onClick={() => handleApplyPreset(20)}
          className="rounded px-1.5 py-0.5 text-[11px] font-semibold bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 transition-colors"
          title="Prix avec markup +20% sur coût TTC (arrondi à 10 millimes)"
        >
          20%
        </button>
        <button
          type="button"
          onClick={() => handleApplyPreset(25)}
          className="rounded px-1.5 py-0.5 text-[11px] font-semibold bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200 transition-colors"
          title="Prix avec markup +25% sur coût TTC (arrondi à 10 millimes)"
        >
          25%
        </button>
        <button
          type="button"
          onClick={() => handleApplyPreset(30)}
          className="rounded px-1.5 py-0.5 text-[11px] font-semibold bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200 transition-colors"
          title="Prix avec markup +30% sur coût TTC (arrondi à 10 millimes)"
        >
          30%
        </button>
      </div>

      <div className="flex items-center gap-1.5">
        <div className="relative flex-1">
          <input
            type="text"
            value={dinarsInput}
            onChange={(e) => setDinarsInput(e.target.value)}
            className="w-full rounded-md border border-border px-2 py-1 text-right font-mono text-xs font-bold text-ink focus:border-herb-600 focus:outline-none focus:ring-1 focus:ring-herb-600"
            placeholder="0.000"
          />
          <span className="pointer-events-none absolute left-2 top-1.5 text-[10px] font-bold text-ink-muted">
            DT
          </span>
        </div>

        <Button
          type="button"
          size="sm"
          variant="primary"
          onClick={handlePush}
          disabled={mutation.isPending || !candidateMillimes}
          className="shrink-0 text-xs px-2.5 py-1 font-semibold"
          title="Appliquer immédiatement ce prix de vente sur WooCommerce"
        >
          {mutation.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <>
              <Send className="h-3 w-3 mr-1" />
              Pousser
            </>
          )}
        </Button>
      </div>

      {prospectiveMarkup !== null && prospectiveMarkup !== row.markupRate && (
        <div className="flex items-center justify-between text-[11px] font-medium">
          <span className="text-ink-muted">Nouveau markup :</span>
          <span
            className={
              prospectiveMarkup < 20
                ? 'text-rose-600 font-bold'
                : prospectiveMarkup < 30
                  ? 'text-amber-700 font-bold'
                  : 'text-emerald-700 font-bold'
            }
          >
            +{prospectiveMarkup.toFixed(1)}%{' '}
            {prospectiveMargin !== null && (
              <span className="font-normal text-[10px] text-ink-ghost">({prospectiveMargin.toFixed(1)}% marge)</span>
            )}
          </span>
        </div>
      )}

      {feedback && (
        <div
          className={`flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded ${
            feedback.tone === 'ok'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-rose-50 text-rose-800 border border-rose-200'
          }`}
        >
          {feedback.tone === 'ok' ? (
            <Check className="h-3 w-3 shrink-0 text-emerald-600" />
          ) : (
            <AlertTriangle className="h-3 w-3 shrink-0 text-rose-600" />
          )}
          <span>{feedback.message}</span>
        </div>
      )}

      {row.lastPriceChange && (
        <div className="pt-1 mt-1 border-t border-border/40 flex items-center justify-between text-[10px]">
          <span className="text-ink-muted flex items-center gap-1">
            <span
              className={`inline-block px-1 py-0.2 rounded text-[9px] font-extrabold ${
                row.lastPriceChange.authorBadge.isApp
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'bg-amber-50 text-amber-800 border border-amber-200'
              }`}
            >
              {row.lastPriceChange.authorBadge.isApp ? 'App' : 'Woo'}
            </span>
            <span className="truncate max-w-[110px] font-medium" title={row.lastPriceChange.authorBadge.user}>
              {row.lastPriceChange.authorBadge.user}
            </span>
          </span>
          <span className="text-ink-ghost text-[9px]">
            {new Date(row.lastPriceChange.detectedAt).toLocaleDateString('fr-FR', {
              day: '2-digit',
              month: '2-digit',
            })}
          </span>
        </div>
      )}
    </div>
  );
}

export function MargeControlPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const searchInputId = useId();

  // Filters state
  const [searchInput, setSearchInput] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [excludedSubBrands, setExcludedSubBrands] = useState<string[]>([]);
  const [marginFilter, setMarginFilter] = useState<'all' | 'critical' | 'normal' | 'high' | 'promo'>('all');
  const [sort, setSort] = useState<'margin_asc' | 'margin_desc' | 'name_asc'>('margin_asc');
  const [page, setPage] = useState<number>(1);
  const pageSize = 50;

  // Bulk actions state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);
  const [bulkFeedback, setBulkFeedback] = useState<{ tone: 'ok' | 'error'; message: string } | null>(null);
  const [confirmBrandModal, setConfirmBrandModal] = useState<{
    brand: string;
    targetMarkup: number;
    count: number;
  } | null>(null);

  // Price history modal state
  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
  const [historySearch, setHistorySearch] = useState<string>('');
  const [historySourceFilter, setHistorySourceFilter] = useState<'all' | 'app' | 'woo'>('all');
  const [historyFilterProductId, setHistoryFilterProductId] = useState<number | null>(null);
  const [historyPage, setHistoryPage] = useState<number>(1);

  const historyQuery = useQuery({
    queryKey: ['price-history', historySearch, historySourceFilter, historyFilterProductId, historyPage],
    queryFn: () =>
      fetchPriceHistory({
        search: historySearch || undefined,
        sourceFilter: historySourceFilter,
        productId: historyFilterProductId || undefined,
        page: historyPage,
        pageSize: 30,
      }),
    enabled: showHistoryModal,
  });

  // Query backend
  const query = useQuery({
    queryKey: ['marge-control', search, selectedBrand, excludedSubBrands, marginFilter, sort, page, pageSize],
    queryFn: () =>
      fetchMargeControl({
        search: search || undefined,
        brand: selectedBrand !== 'all' ? selectedBrand : undefined,
        excludedSubBrands: excludedSubBrands.length > 0 ? excludedSubBrands.join(',') : undefined,
        marginFilter,
        sort,
        page,
        pageSize,
      }),
  });

  const totals: MargeControlTotalsDto | undefined = query.data?.totals;
  const rows = query.data?.rows ?? [];
  const brands = query.data?.brands ?? [];
  const pagination = query.data?.pagination;

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setSearch('');
    setSelectedBrand('all');
    setExcludedSubBrands([]);
    setMarginFilter('all');
    setSort('margin_asc');
    setPage(1);
    setSelectedIds(new Set());
  };

  // Selection helpers
  const allPageIds = rows.map((r) => r.productId);
  const isAllPageSelected = allPageIds.length > 0 && allPageIds.every((id) => selectedIds.has(id));

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllPage = () => {
    if (isAllPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of allPageIds) next.delete(id);
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of allPageIds) next.add(id);
        return next;
      });
    }
  };

  // Bulk Apply to selection
  const handleBulkApplyToSelection = async (targetMarkupPct: number) => {
    const selectedRows = rows.filter((r) => selectedIds.has(r.productId));
    if (selectedRows.length === 0) return;

    setBulkLoading(true);
    setBulkFeedback(null);
    try {
      const items = selectedRows.map((r) => {
        const targetPrice = computeTargetMarkupPriceMillimes(r.effectiveCostTtc, targetMarkupPct);
        return {
          productId: r.productId,
          regularPriceTtc: targetPrice,
          salePriceTtc: null,
        };
      });

      const res = await bulkUpdateMargeControlPrice({ items });
      setBulkFeedback({
        tone: res.ok ? 'ok' : 'error',
        message:
          res.message ||
          `${res.updatedCount} produit(s) mis à jour avec succès sur WooCommerce (+${targetMarkupPct}% markup)`,
      });
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['marge-control'] });
    } catch (err: unknown) {
      const errObj = (typeof err === 'object' && err !== null ? err : {}) as { message?: string };
      setBulkFeedback({
        tone: 'error',
        message: errObj.message || 'Échec de mise à jour groupée sur WooCommerce',
      });
    } finally {
      setBulkLoading(false);
    }
  };

  // Bulk Apply to entire Brand
  const handleConfirmBrandBulk = async () => {
    if (!confirmBrandModal) return;
    setBulkLoading(true);
    try {
      const res = await bulkUpdateMargeControlPrice({
        brand: confirmBrandModal.brand,
        targetMarkupPct: confirmBrandModal.targetMarkup,
        excludedSubBrands: excludedSubBrands.length > 0 ? excludedSubBrands.join(',') : undefined,
      });
      setBulkFeedback({
        tone: res.ok ? 'ok' : 'error',
        message:
          res.message ||
          `${res.updatedCount} produit(s) de la marque ${confirmBrandModal.brand} mis à jour avec succès sur WooCommerce (+${confirmBrandModal.targetMarkup}% markup)`,
      });
      setConfirmBrandModal(null);
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['marge-control'] });
    } catch (err: unknown) {
      const errObj = (typeof err === 'object' && err !== null ? err : {}) as { message?: string };
      setBulkFeedback({
        tone: 'error',
        message: errObj.message || 'Échec de mise à jour de la marque sur WooCommerce',
      });
    } finally {
      setBulkLoading(false);
    }
  };

  const renderPagination = () => {
    if (!pagination || pagination.totalPages <= 1) return null;
    const current = pagination.page;
    const totalPages = pagination.totalPages;

    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2 text-xs text-ink shadow-xs">
        <span className="font-medium text-ink-muted">
          Page <strong className="text-ink font-bold">{current}</strong> sur{' '}
          <strong className="text-ink font-bold">{totalPages}</strong> ({pagination.totalItems} produits)
        </span>
        <div className="flex items-center gap-1 font-sans">
          <button
            type="button"
            onClick={() => setPage(1)}
            disabled={current <= 1}
            className="rounded border border-border bg-card px-2 py-1 text-xs font-semibold text-ink hover:bg-herb-50 hover:text-herb-800 disabled:opacity-40 disabled:pointer-events-none transition-colors"
          >
            « Premier
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={current <= 1}
            className="rounded border border-border bg-card px-2.5 py-1 text-xs font-semibold text-ink hover:bg-herb-50 hover:text-herb-800 disabled:opacity-40 disabled:pointer-events-none transition-colors"
          >
            ‹ Précédent
          </button>
          <span className="rounded bg-herb-700 px-2.5 py-1 text-xs font-bold text-white shadow-xs">
            {current}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={current >= totalPages}
            className="rounded border border-border bg-card px-2.5 py-1 text-xs font-semibold text-ink hover:bg-herb-50 hover:text-herb-800 disabled:opacity-40 disabled:pointer-events-none transition-colors"
          >
            Suivant ›
          </button>
          <button
            type="button"
            onClick={() => setPage(totalPages)}
            disabled={current >= totalPages}
            className="rounded border border-border bg-card px-2 py-1 text-xs font-semibold text-ink hover:bg-herb-50 hover:text-herb-800 disabled:opacity-40 disabled:pointer-events-none transition-colors"
          >
            Dernier »
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 pb-12">
      <PageHeader
        index="S32"
        title="Contrôle des Marges & Rentabilité Woo"
        purpose="Surveillance en temps réel des marges et markups WooCommerce par rapport aux coûts d'achat réels. Ajustement direct 1 par 1 ou par marque complète avec arrondi automatique à 10 millimes."
        actions={
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setHistoryFilterProductId(null);
                setHistoryPage(1);
                setShowHistoryModal(true);
              }}
              className="border-border text-ink hover:bg-slate-100"
            >
              <History className="h-3.5 w-3.5 mr-1.5 text-blue-600" />
              Historique des Prix &amp; Auteurs
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
            >
              <RotateCcw className={`h-3.5 w-3.5 mr-1.5 ${query.isFetching ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        }
      />

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <div
          role="button"
          tabIndex={0}
          onClick={() => {
            setMarginFilter('all');
            setPage(1);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setMarginFilter('all');
              setPage(1);
            }
          }}
          className={`cursor-pointer rounded-xl border p-3 shadow-xs transition-all ${
            marginFilter === 'all'
              ? 'border-herb-700 bg-herb-50/50 ring-2 ring-herb-700/20'
              : 'border-border bg-card hover:border-herb-300'
          }`}
        >
          <div className="flex items-center justify-between text-xs font-medium text-ink-muted">
            <span>Produits avec coût</span>
            <Percent className="h-4 w-4 text-herb-700" />
          </div>
          <div className="mt-2 text-2xl font-bold tracking-tight text-ink">
            {totals ? totals.totalWithCost.toLocaleString('fr-TN') : '—'}
          </div>
          <p className="mt-1 text-[11px] text-ink-ghost">Base de calcul active</p>
        </div>

        <div
          role="button"
          tabIndex={0}
          onClick={() => {
            setMarginFilter('critical');
            setPage(1);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setMarginFilter('critical');
              setPage(1);
            }
          }}
          className={`cursor-pointer rounded-xl border p-3 shadow-xs transition-all ${
            marginFilter === 'critical'
              ? 'border-rose-600 bg-rose-50 ring-2 ring-rose-600/20'
              : 'border-rose-200 bg-rose-50/40 hover:border-rose-400'
          }`}
        >
          <div className="flex items-center justify-between text-xs font-semibold text-rose-800">
            <span>Critiques (&lt; 20% markup)</span>
            <AlertTriangle className="h-4 w-4 text-rose-600" />
          </div>
          <div className="mt-2 text-2xl font-bold tracking-tight text-rose-700">
            {totals ? totals.criticalCount.toLocaleString('fr-TN') : '—'}
          </div>
          <p className="mt-1 text-[11px] font-medium text-rose-600">Urgence ajustement</p>
        </div>

        <div
          role="button"
          tabIndex={0}
          onClick={() => {
            setMarginFilter('normal');
            setPage(1);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setMarginFilter('normal');
              setPage(1);
            }
          }}
          className={`cursor-pointer rounded-xl border p-3 shadow-xs transition-all ${
            marginFilter === 'normal'
              ? 'border-amber-600 bg-amber-50 ring-2 ring-amber-600/20'
              : 'border-border bg-card hover:border-amber-400'
          }`}
        >
          <div className="flex items-center justify-between text-xs font-semibold text-amber-900">
            <span>Sains (20% - 30% markup)</span>
            <CheckCircle2 className="h-4 w-4 text-amber-600" />
          </div>
          <div className="mt-2 text-2xl font-bold tracking-tight text-amber-900">
            {totals ? totals.normalCount.toLocaleString('fr-TN') : '—'}
          </div>
          <p className="mt-1 text-[11px] text-ink-ghost">Marge standard</p>
        </div>

        <div
          role="button"
          tabIndex={0}
          onClick={() => {
            setMarginFilter('high');
            setPage(1);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setMarginFilter('high');
              setPage(1);
            }
          }}
          className={`cursor-pointer rounded-xl border p-3 shadow-xs transition-all ${
            marginFilter === 'high'
              ? 'border-emerald-600 bg-emerald-50 ring-2 ring-emerald-600/20'
              : 'border-border bg-card hover:border-emerald-400'
          }`}
        >
          <div className="flex items-center justify-between text-xs font-semibold text-emerald-900">
            <span>Forts (&gt; 30% markup)</span>
            <TrendingUp className="h-4 w-4 text-emerald-600" />
          </div>
          <div className="mt-2 text-2xl font-bold tracking-tight text-emerald-800">
            {totals ? totals.highCount.toLocaleString('fr-TN') : '—'}
          </div>
          <p className="mt-1 text-[11px] text-ink-ghost">Très rentable</p>
        </div>

        <div
          role="button"
          tabIndex={0}
          onClick={() => {
            setMarginFilter('promo');
            setPage(1);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setMarginFilter('promo');
              setPage(1);
            }
          }}
          className={`cursor-pointer rounded-xl border p-3 shadow-xs transition-all ${
            marginFilter === 'promo'
              ? 'border-purple-600 bg-purple-50 ring-2 ring-purple-600/20'
              : 'border-purple-200 bg-purple-50/40 hover:border-purple-400'
          }`}
        >
          <div className="flex items-center justify-between text-xs font-semibold text-purple-900">
            <span>Promos Dépôt</span>
            <Sparkles className="h-4 w-4 text-purple-600" />
          </div>
          <div className="mt-2 text-2xl font-bold tracking-tight text-purple-800">
            {totals ? totals.promoCount.toLocaleString('fr-TN') : '—'}
          </div>
          <p className="mt-1 text-[11px] font-medium text-purple-700">10+1, 6+1 calculés</p>
        </div>

        <div className="rounded-xl border border-border bg-card p-3 shadow-xs">
          <div className="flex items-center justify-between text-xs font-medium text-ink-muted">
            <span>Markup Moyen</span>
            <Tag className="h-4 w-4 text-herb-700" />
          </div>
          <div className="mt-2 text-2xl font-bold tracking-tight text-herb-800">
            {totals ? `+${totals.avgMarginPct.toFixed(1)}%` : '—'}
          </div>
          <p className="mt-1 text-[11px] text-ink-ghost">Catalogue global</p>
        </div>
      </div>

      {/* Filter and Control Bar */}
      <div className="rounded-xl border border-border bg-card p-4 shadow-xs space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search bar */}
          <form onSubmit={handleSearchSubmit} className="flex-1 min-w-[240px]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-ink-muted" />
              <input
                id={searchInputId}
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Rechercher par nom, SKU ou code-barres EAN..."
                className="w-full rounded-lg border border-border bg-paper py-2 pl-9 pr-3 text-xs text-ink placeholder-ink-ghost focus:border-herb-600 focus:outline-none focus:ring-1 focus:ring-herb-600"
              />
            </div>
          </form>

          {/* Brand select */}
          <div className="w-48">
            <select
              value={selectedBrand}
              onChange={(e) => {
                setSelectedBrand(e.target.value);
                setExcludedSubBrands([]);
                setPage(1);
                setSelectedIds(new Set());
              }}
              className="w-full rounded-lg border border-border bg-paper py-2 px-3 text-xs font-medium text-ink focus:border-herb-600 focus:outline-none focus:ring-1 focus:ring-herb-600"
            >
              <option value="all">Toutes les marques ({brands.length})</option>
              {brands.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>

          {/* Sort select */}
          <div className="w-56">
            <select
              value={sort}
              onChange={(e) => {
                setSort(e.target.value as 'margin_asc' | 'margin_desc' | 'name_asc');
                setPage(1);
              }}
              className="w-full rounded-lg border border-border bg-paper py-2 px-3 text-xs font-medium text-ink focus:border-herb-600 focus:outline-none focus:ring-1 focus:ring-herb-600"
            >
              <option value="margin_asc">Markup : Plus faibles d'abord (urgence)</option>
              <option value="margin_desc">Markup : Plus forts d'abord</option>
              <option value="name_asc">Nom de produit (A → Z)</option>
            </select>
          </div>

          {/* Reset button */}
          {(search ||
            selectedBrand !== 'all' ||
            excludedSubBrands.length > 0 ||
            marginFilter !== 'all' ||
            sort !== 'margin_asc') && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleResetFilters}
              className="shrink-0 text-xs"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1" />
              Réinitialiser
            </Button>
          )}
        </div>

        {/* Sub-brands & ranges checkbox pills */}
        <SubBrandPills
          brand={selectedBrand}
          excludedSubBrands={excludedSubBrands}
          onChange={(ex) => {
            setExcludedSubBrands(ex);
            setPage(1);
            setSelectedIds(new Set());
          }}
        />

        {/* Tab pills for margin filters */}
        <div className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-border/60">
          <span className="text-xs font-medium text-ink-muted mr-1">Filtrer par statut :</span>
          <button
            type="button"
            onClick={() => {
              setMarginFilter('all');
              setPage(1);
            }}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              marginFilter === 'all'
                ? 'bg-herb-700 text-white font-semibold shadow-xs'
                : 'bg-paper text-ink hover:bg-card border border-border'
            }`}
          >
            Tous les produits
          </button>
          <button
            type="button"
            onClick={() => {
              setMarginFilter('critical');
              setPage(1);
            }}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              marginFilter === 'critical'
                ? 'bg-rose-700 text-white font-semibold shadow-xs'
                : 'bg-rose-50 text-rose-800 hover:bg-rose-100 border border-rose-200'
            }`}
          >
            Marges critiques (&lt; 20% markup)
          </button>
          <button
            type="button"
            onClick={() => {
              setMarginFilter('normal');
              setPage(1);
            }}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              marginFilter === 'normal'
                ? 'bg-amber-700 text-white font-semibold shadow-xs'
                : 'bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200'
            }`}
          >
            Saines (20% - 30% markup)
          </button>
          <button
            type="button"
            onClick={() => {
              setMarginFilter('high');
              setPage(1);
            }}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              marginFilter === 'high'
                ? 'bg-emerald-700 text-white font-semibold shadow-xs'
                : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200'
            }`}
          >
            Fortes (&gt; 30% markup)
          </button>
          <button
            type="button"
            onClick={() => {
              setMarginFilter('promo');
              setPage(1);
            }}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              marginFilter === 'promo'
                ? 'bg-purple-700 text-white font-semibold shadow-xs'
                : 'bg-purple-50 text-purple-800 hover:bg-purple-100 border border-purple-200'
            }`}
          >
            Avec Promo Depot (10+1, 6+1...)
          </button>
        </div>
      </div>

      {/* Bulk Feedback Banner */}
      {bulkFeedback && (
        <div
          className={`flex items-center justify-between rounded-xl border p-3.5 text-xs font-semibold shadow-xs ${
            bulkFeedback.tone === 'ok'
              ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
              : 'border-rose-300 bg-rose-50 text-rose-900'
          }`}
        >
          <div className="flex items-center gap-2">
            {bulkFeedback.tone === 'ok' ? (
              <Check className="h-4 w-4 text-emerald-600 shrink-0" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-rose-600 shrink-0" />
            )}
            <span>{bulkFeedback.message}</span>
          </div>
          <button
            type="button"
            onClick={() => setBulkFeedback(null)}
            className="text-xs text-ink-muted hover:text-ink p-1"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Dedicated Brand Bulk Action Bar */}
      {selectedBrand !== 'all' && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50/70 p-3.5 shadow-xs">
          <div className="flex items-center gap-2.5">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-blue-600 text-white shadow-xs">
              <Layers className="h-4 w-4" />
            </div>
            <div>
              <span className="text-xs font-bold text-blue-950">
                Marque active : {selectedBrand} ({totals?.totalWithCost ?? 0} produits avec coût)
              </span>
              <p className="text-[11px] text-blue-800">
                Ajuster et pousser tous les produits de cette marque vers WooCommerce avec arrondi à 10 millimes
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-blue-900">Appliquer à toute la marque :</span>
            <Button
              type="button"
              size="sm"
              onClick={() =>
                setConfirmBrandModal({
                  brand: selectedBrand,
                  targetMarkup: 20,
                  count: totals?.totalWithCost ?? 0,
                })
              }
              disabled={bulkLoading}
              className="bg-white text-rose-700 border border-rose-200 hover:bg-rose-50 text-xs font-bold shadow-xs"
            >
              +20% toute la marque
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() =>
                setConfirmBrandModal({
                  brand: selectedBrand,
                  targetMarkup: 25,
                  count: totals?.totalWithCost ?? 0,
                })
              }
              disabled={bulkLoading}
              className="bg-white text-amber-800 border border-amber-200 hover:bg-amber-50 text-xs font-bold shadow-xs"
            >
              +25% toute la marque
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() =>
                setConfirmBrandModal({
                  brand: selectedBrand,
                  targetMarkup: 30,
                  count: totals?.totalWithCost ?? 0,
                })
              }
              disabled={bulkLoading}
              className="bg-white text-emerald-800 border border-emerald-200 hover:bg-emerald-50 text-xs font-bold shadow-xs"
            >
              +30% toute la marque
            </Button>
          </div>
        </div>
      )}

      {/* Checkbox Bulk Selection Action Bar */}
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-herb-300 bg-herb-50/90 px-4 py-2.5 shadow-sm">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center rounded-full bg-herb-700 px-2 py-0.5 text-xs font-bold text-white shadow-xs">
              {selectedIds.size}
            </span>
            <span className="text-xs font-bold text-herb-900">
              produit(s) sélectionné(s) — Pousser vers WooCommerce :
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => handleBulkApplyToSelection(20)}
              disabled={bulkLoading}
              className="text-xs font-semibold bg-white hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300 shadow-xs"
            >
              {bulkLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
              Appliquer +20%
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => handleBulkApplyToSelection(25)}
              disabled={bulkLoading}
              className="text-xs font-semibold bg-white hover:bg-amber-50 hover:text-amber-800 hover:border-amber-300 shadow-xs"
            >
              {bulkLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
              Appliquer +25%
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => handleBulkApplyToSelection(30)}
              disabled={bulkLoading}
              className="text-xs font-semibold bg-white hover:bg-emerald-50 hover:text-emerald-800 hover:border-emerald-300 shadow-xs"
            >
              {bulkLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
              Appliquer +30%
            </Button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="ml-2 text-xs font-medium text-ink-muted hover:text-ink underline"
            >
              Désélectionner tout
            </button>
          </div>
        </div>
      )}

      {/* Top Pagination */}
      {renderPagination()}

      {/* Main Table Shell */}
      <TableShell>
        {query.isLoading ? (
          <div className="py-20">
            <Loading label="Chargement des marges et des coûts d'achat..." />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center">
            <PackageCheck className="mx-auto h-10 w-10 text-ink-ghost" />
            <p className="mt-3 text-sm font-semibold text-ink">Aucun produit ne correspond à ces critères</p>
            <p className="mt-1 text-xs text-ink-muted">
              Essayez d'élargir vos filtres ou de réinitialiser la recherche.
            </p>
          </div>
        ) : (
          <Table>
            <THead>
              <Tr>
                <Th className="w-[44px] text-center">
                  <button
                    type="button"
                    onClick={toggleSelectAllPage}
                    className="p-1 text-ink-muted hover:text-ink focus:outline-none"
                    title={isAllPageSelected ? 'Tout désélectionner' : 'Tout sélectionner sur la page'}
                  >
                    {isAllPageSelected ? (
                      <CheckSquare className="h-4 w-4 text-herb-700" />
                    ) : (
                      <Square className="h-4 w-4" />
                    )}
                  </button>
                </Th>
                <Th className="w-[300px]">Produit & Barcode</Th>
                <Th className="w-[170px]">Coût Achat Base</Th>
                <Th className="w-[180px]">Promo Dépôt & Coût Réel</Th>
                <Th className="w-[150px]">Prix Vente Woo</Th>
                <Th className="w-[170px]">Markup & Marge</Th>
                <Th className="w-[240px]">Ajustement Rapide & Push Woo</Th>
              </Tr>
            </THead>
            <TBody>
              {rows.map((row) => {
                const isCritical = row.markupRate < 20;
                const isModerate = row.markupRate >= 20 && row.markupRate < 30;
                const isChecked = selectedIds.has(row.productId);

                return (
                  <Tr
                    key={row.productId}
                    className={
                      isChecked
                        ? 'bg-herb-50/60 hover:bg-herb-50/80'
                        : isCritical
                          ? 'bg-rose-50/20 hover:bg-rose-50/40'
                          : isModerate
                            ? 'bg-amber-50/10 hover:bg-amber-50/30'
                            : 'hover:bg-card/70'
                    }
                  >
                    {/* Checkbox column */}
                    <Td className="text-center">
                      <button
                        type="button"
                        onClick={() => toggleSelect(row.productId)}
                        className="p-1 text-ink-muted hover:text-ink focus:outline-none"
                      >
                        {isChecked ? (
                          <CheckSquare className="h-4 w-4 text-herb-700" />
                        ) : (
                          <Square className="h-4 w-4" />
                        )}
                      </button>
                    </Td>

                    {/* Product & Packshot */}
                    <Td>
                      <div className="flex items-start gap-3">
                        {row.image ? (
                          <img
                            src={row.image}
                            alt=""
                            className="h-16 w-16 shrink-0 rounded-lg border border-border object-contain bg-white p-1 shadow-xs"
                            loading="lazy"
                          />
                        ) : (
                          <div className="grid h-16 w-16 shrink-0 place-items-center rounded-lg border border-border bg-paper text-ink-ghost">
                            <Tag className="h-6 w-6" />
                          </div>
                        )}
                        <div className="min-w-0 space-y-1">
                          <p className="font-semibold text-xs leading-snug text-ink line-clamp-2">
                            {row.name}
                          </p>
                          <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-ink-muted">
                            {row.brand && (
                              <span className="font-bold text-herb-800 bg-herb-50 border border-herb-200 rounded px-1.5 py-0.5">
                                {row.brand}
                              </span>
                            )}
                            <span className="font-mono text-ink-ghost">SKU: {row.sku}</span>
                          </div>
                          <div className="flex items-center gap-1 font-mono text-[11px] text-ink-muted">
                            <span>EAN :</span>
                            <span className="font-semibold text-ink">
                              {row.gtinCanonical || row.barcodeRaw || row.eanRaw || '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Td>

                    {/* Base Cost HT & TTC */}
                    <Td>
                      <div className="space-y-1">
                        <div className="flex items-baseline justify-between">
                          <span className="text-[11px] text-ink-muted">Achat HT :</span>
                          <span className="font-mono text-xs font-bold text-ink">
                            {formatMillimes(row.baseCostHt)}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between">
                          <span className="text-[11px] text-ink-muted">TVA :</span>
                          <span className="rounded bg-paper border border-border px-1 py-0.5 font-mono text-[10px] font-semibold text-ink">
                            {row.vatRate}%
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between border-t border-border/50 pt-1">
                          <span className="text-[11px] font-medium text-ink-muted">Coût TTC :</span>
                          <span className="font-mono text-xs font-bold text-ink-muted">
                            {formatMillimes(row.baseCostTtc)}
                          </span>
                        </div>
                        {row.purchaseSourceLabel && (
                          <p className="text-[10px] text-ink-ghost truncate" title={row.purchaseSourceLabel}>
                            Source : {row.purchaseSourceLabel}
                          </p>
                        )}
                      </div>
                    </Td>

                    {/* Depot Promo & Effective Cost */}
                    <Td>
                      {row.hasDepotPromo ? (
                        <div className="space-y-1.5 rounded-lg border border-purple-200 bg-purple-50/60 p-2">
                          <div className="flex items-center gap-1 text-[11px] font-bold text-purple-900">
                            <Sparkles className="h-3.5 w-3.5 text-purple-600 shrink-0" />
                            <span>{row.promoLabel}</span>
                          </div>
                          {row.promoBuyQty && row.promoFreeQty ? (
                            <p className="text-[10px] text-purple-700">
                              Achetez {row.promoBuyQty} + {row.promoFreeQty} Offert(s)
                            </p>
                          ) : row.promoPercent ? (
                            <p className="text-[10px] text-purple-700">Remise -{row.promoPercent}%</p>
                          ) : null}
                          <div className="border-t border-purple-200/80 pt-1">
                            <div className="flex items-baseline justify-between text-[11px]">
                              <span className="font-medium text-purple-900">Coût réel TTC :</span>
                              <span className="font-mono text-xs font-bold text-purple-950">
                                {formatMillimes(row.effectiveCostTtc)}
                              </span>
                            </div>
                            <p className="text-[10px] text-purple-600 text-right">
                              (Gain : {formatMillimes(row.baseCostTtc - row.effectiveCostTtc)})
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-2 text-[11px] text-ink-ghost italic">
                          Aucune promo dépôt
                        </div>
                      )}
                    </Td>

                    {/* Selling Price Woo */}
                    <Td>
                      <div className="space-y-1">
                        <div className="flex items-baseline gap-1.5">
                          <span className="font-mono text-sm font-bold text-ink">
                            {formatMillimes(row.sellingPriceTtc)}
                          </span>
                          {row.isSellingPromo && (
                            <span className="rounded bg-rose-600 px-1 py-0.2 text-[10px] font-bold text-white uppercase">
                              Promo
                            </span>
                          )}
                        </div>
                        {row.isSellingPromo && row.regularPriceTtc > row.sellingPriceTtc && (
                          <p className="text-[11px] text-ink-ghost line-through font-mono">
                            Normal : {formatMillimes(row.regularPriceTtc)}
                          </p>
                        )}
                        <p className="text-[10px] text-ink-ghost font-mono">ID Woo : #{row.wcProductId}</p>
                      </div>
                    </Td>

                    {/* Current Markup & Margin */}
                    <Td>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold shadow-xs ${
                              isCritical
                                ? 'bg-rose-100 text-rose-800 border border-rose-300'
                                : isModerate
                                  ? 'bg-amber-100 text-amber-800 border border-amber-300'
                                  : 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                            }`}
                          >
                            {isCritical && <AlertTriangle className="h-3 w-3 shrink-0 text-rose-600" />}
                            +{row.markupRate.toFixed(1)}%
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between text-[11px]">
                          <span className="text-ink-muted">Marge brute :</span>
                          <span
                            className={`font-mono text-xs font-bold ${
                              isCritical ? 'text-rose-700' : 'text-emerald-800'
                            }`}
                          >
                            {formatMillimes(row.marginAmountTtc)}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between text-[10px] text-ink-ghost">
                          <span>Taux marge :</span>
                          <span className="font-mono font-medium">{row.marginRate.toFixed(1)}%</span>
                        </div>
                      </div>
                    </Td>

                    {/* Direct Price Adjustment & Push */}
                    <Td>
                      <PriceAdjustmentCell
                        row={row}
                        onUpdated={() => {
                          queryClient.invalidateQueries({ queryKey: ['marge-control'] });
                        }}
                      />
                    </Td>
                  </Tr>
                );
              })}
            </TBody>
          </Table>
        )}
      </TableShell>

      {/* Bottom Pagination */}
      {renderPagination()}


      {/* Price History & Author Audit Modal */}
      {showHistoryModal &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs overflow-y-auto animate-fade"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowHistoryModal(false);
              }
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              className="w-full max-w-4xl max-h-[90vh] flex flex-col rounded-2xl border border-border bg-card p-6 shadow-2xl my-auto animate-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between pb-3 border-b border-border">
                <div>
                  <h3 className="text-base font-bold text-ink flex items-center gap-2">
                    <History className="h-4 w-4 text-blue-600" />
                    Historique des Variations de Prix &amp; Auteurs
                  </h3>
                  <p className="text-xs text-ink-muted mt-0.5">
                    Traçabilité en temps réel des prix modifiés depuis l'App (avec utilisateur authentifié) ou directement sur WooCommerce.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowHistoryModal(false)}
                  className="rounded-lg p-1.5 text-ink-muted hover:bg-paper hover:text-ink cursor-pointer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Filters Bar */}
              <div className="py-3 flex flex-wrap items-center justify-between gap-3 border-b border-border/50">
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      setHistorySourceFilter('all');
                      setHistoryPage(1);
                    }}
                    className={`px-2.5 py-1 text-xs font-semibold rounded-lg transition-all ${
                      historySourceFilter === 'all'
                        ? 'bg-ink text-white'
                        : 'bg-paper text-ink-muted hover:text-ink'
                    }`}
                  >
                    Tous les changements
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setHistorySourceFilter('app');
                      setHistoryPage(1);
                    }}
                    className={`px-2.5 py-1 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 ${
                      historySourceFilter === 'app'
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                    }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                    Modifiés depuis l'App (hazgui@1405.tn…)
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setHistorySourceFilter('woo');
                      setHistoryPage(1);
                    }}
                    className={`px-2.5 py-1 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 ${
                      historySourceFilter === 'woo'
                        ? 'bg-amber-600 text-white'
                        : 'bg-amber-50 text-amber-800 hover:bg-amber-100'
                    }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                    Modifiés sur WooCommerce Back-office
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  {historyFilterProductId && (
                    <button
                      type="button"
                      onClick={() => setHistoryFilterProductId(null)}
                      className="text-xs font-bold text-rose-600 hover:underline flex items-center gap-1"
                    >
                      <X className="h-3.5 w-3.5" />
                      Effacer filtre produit
                    </button>
                  )}
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-2 h-3.5 w-3.5 text-ink-muted" />
                    <input
                      type="text"
                      placeholder="Rechercher produit, SKU, auteur…"
                      value={historySearch}
                      onChange={(e) => {
                        setHistorySearch(e.target.value);
                        setHistoryPage(1);
                      }}
                      className="rounded-lg border border-border bg-paper pl-8 pr-3 py-1.5 text-xs text-ink placeholder:text-ink-ghost focus:border-blue-500 focus:outline-none w-56"
                    />
                  </div>
                </div>
              </div>

              {/* Table / List */}
              <div className="flex-1 overflow-y-auto py-2 min-h-[300px]">
                {historyQuery.isLoading ? (
                  <div className="flex items-center justify-center py-16 text-ink-muted text-xs gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    Chargement de l'historique des prix…
                  </div>
                ) : !historyQuery.data || historyQuery.data.items.length === 0 ? (
                  <div className="py-16 text-center text-xs text-ink-muted">
                    Aucun historique de variation de prix trouvé avec ces critères.
                  </div>
                ) : (
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-border bg-paper text-ink-muted font-bold">
                        <th className="p-2.5">Date &amp; Heure</th>
                        <th className="p-2.5">Produit &amp; Référence</th>
                        <th className="p-2.5">Prix Régulier TTC</th>
                        <th className="p-2.5">Variation</th>
                        <th className="p-2.5">Auteur &amp; Origine</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                      {historyQuery.data.items.map((item) => (
                        <tr key={item.id} className="hover:bg-paper/50 transition-colors">
                          <td className="p-2.5 whitespace-nowrap text-ink-muted font-mono text-[11px]">
                            {new Date(item.detectedAt).toLocaleString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </td>
                          <td className="p-2.5">
                            <div className="font-bold text-ink text-xs line-clamp-1">{item.productName}</div>
                            <div className="flex items-center gap-1.5 text-[10px] text-ink-muted mt-0.5">
                              {item.brand && (
                                <span className="font-semibold text-emerald-800">{item.brand}</span>
                              )}
                              <span>•</span>
                              <span className="font-mono">SKU: {item.sku}</span>
                            </div>
                          </td>
                          <td className="p-2.5 whitespace-nowrap">
                            <div className="text-[11px] text-ink-ghost line-through">
                              {item.oldRegularTtc !== null ? formatMillimes(item.oldRegularTtc) : '—'}
                            </div>
                            <div className="font-bold text-ink text-xs">
                              {item.newRegularTtc !== null ? formatMillimes(item.newRegularTtc) : '—'}
                            </div>
                          </td>
                          <td className="p-2.5 whitespace-nowrap">
                            {item.diffRegularTtc !== null ? (
                              <span
                                className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-bold ${
                                  item.diffRegularTtc > 0
                                    ? 'bg-emerald-50 text-emerald-700'
                                    : item.diffRegularTtc < 0
                                      ? 'bg-rose-50 text-rose-700'
                                      : 'bg-slate-50 text-slate-700'
                                }`}
                              >
                                {item.diffRegularTtc > 0 ? '+' : ''}
                                {formatMillimes(item.diffRegularTtc)}
                                {item.diffPct !== null ? ` (${item.diffPct > 0 ? '+' : ''}${item.diffPct}%)` : ''}
                              </span>
                            ) : (
                              <span className="text-ink-ghost">—</span>
                            )}
                          </td>
                          <td className="p-2.5">
                            <div className="flex items-center gap-1.5">
                              <span
                                className={`px-2 py-0.5 rounded text-[10px] font-extrabold uppercase tracking-wide ${
                                  item.authorBadge.isApp
                                    ? 'bg-blue-50 text-blue-800 border border-blue-200'
                                    : 'bg-amber-50 text-amber-900 border border-amber-200'
                                }`}
                              >
                                {item.authorBadge.isApp ? 'App MaPara' : 'WooCommerce'}
                              </span>
                              <span className="font-mono font-semibold text-ink text-[11px]">
                                {item.authorBadge.user}
                              </span>
                            </div>
                            <div className="text-[10px] text-ink-muted mt-0.5 font-medium">
                              {item.authorBadge.context}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Footer Pagination */}
              {historyQuery.data && historyQuery.data.total > 0 && (
                <div className="flex items-center justify-between pt-3 border-t border-border text-xs text-ink-muted">
                  <div>
                    Total : <b>{historyQuery.data.total}</b> modification(s) de prix consignée(s)
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={historyPage <= 1 || historyQuery.isFetching}
                      onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                    >
                      Précédent
                    </Button>
                    <span className="font-mono">
                      Page {historyPage} / {Math.ceil(historyQuery.data.total / 30) || 1}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={
                        historyPage >= Math.ceil(historyQuery.data.total / 30) ||
                        historyQuery.isFetching
                      }
                      onClick={() => setHistoryPage((p) => p + 1)}
                    >
                      Suivant
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}

      {/* Confirmation Modal for Brand Bulk Correction */}
      {confirmBrandModal &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs overflow-y-auto animate-fade"
            onClick={(e) => {
              if (e.target === e.currentTarget && !bulkLoading) {
                setConfirmBrandModal(null);
              }
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl space-y-4 my-auto animate-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-ink">
                  Confirmer l'ajustement de marque
                </h3>
                <button
                  type="button"
                  onClick={() => setConfirmBrandModal(null)}
                  className="rounded-lg p-1 text-ink-muted hover:bg-paper hover:text-ink cursor-pointer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-2 text-xs text-ink-muted">
                <p>
                  Vous allez recalculer le prix de tous les produits de la marque{' '}
                  <strong className="text-ink font-bold">{confirmBrandModal.brand}</strong>{' '}
                  ({confirmBrandModal.count} produits avec coût d'achat) avec la règle :
                </p>
                <div className="rounded-lg border border-border bg-paper p-3 space-y-1 font-mono text-ink text-[11px]">
                  <div>• Formule : Coût TTC × (1 + {confirmBrandModal.targetMarkup}%)</div>
                  <div>• Arrondi : multiple de 10 millimes (se terminant par 0)</div>
                  <div>• Destination : synchronisation immédiate sur WooCommerce</div>
                </div>
                <p className="text-rose-700 font-semibold">
                  Cette action appliquera et synchronisera en direct les nouveaux tarifs sur votre boutique WooCommerce.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmBrandModal(null)}
                  disabled={bulkLoading}
                >
                  Annuler
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={bulkLoading}
                  onClick={handleConfirmBrandBulk}
                >
                  {bulkLoading ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      Mise à jour en cours...
                    </>
                  ) : (
                    <>
                      <Send className="h-3.5 w-3.5 mr-1.5" />
                      Pousser vers WooCommerce
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
